import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Clock, TrendUp, TrendDown, Trophy, Flame, Warning, Lightning, Bank, Cpu, ChartLineDown, CalendarCheck, Lock, CheckCircle, Star, ArrowRight } from '@phosphor-icons/react';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer } from 'recharts';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import api from '../lib/api';
import { toast } from 'sonner';
import { showXpToasts } from '../lib/progression';
import { useAuth } from '../context/AuthContext';

// 5 companies per level, rotated from this pool
const CHAOS_COMPANIES = [
  { symbol: 'APEX', name: 'Apex Technologies', sector: 'Tech', basePrice: 245 },
  { symbol: 'NOVA', name: 'Nova Pharmaceuticals', sector: 'Healthcare', basePrice: 128 },
  { symbol: 'VOLT', name: 'Volt Energy', sector: 'Energy', basePrice: 87 },
  { symbol: 'TITAN', name: 'Titan Defense', sector: 'Defense', basePrice: 312 },
  { symbol: 'FLUX', name: 'Flux Financial', sector: 'Finance', basePrice: 156 },
  { symbol: 'ORBIT', name: 'Orbit Space', sector: 'Tech', basePrice: 198 },
  { symbol: 'REEF', name: 'Reef Biotech', sector: 'Healthcare', basePrice: 64 },
  { symbol: 'GRID', name: 'Grid Power', sector: 'Energy', basePrice: 112 },
  { symbol: 'FORGE', name: 'Forge Industries', sector: 'Defense', basePrice: 278 },
  { symbol: 'PULSE', name: 'Pulse Pay', sector: 'Finance', basePrice: 95 },
];

const SCENARIOS = {
  inflation: {
    name: 'Inflation Surge',
    icon: Bank,
    color: 'from-yellow-600 to-red-600',
    desc: 'Rising rates and commodity price shocks',
    news: {
      positive: [
        { text: '{company} raises prices successfully, margins hold', impact: 0.06 },
        { text: '{company} hedged against rate hikes perfectly', impact: 0.05 },
        { text: '{company} reports strong pricing power this quarter', impact: 0.04 },
        { text: 'Consumer spending on {company} products stays resilient', impact: 0.03 },
      ],
      negative: [
        { text: 'Rising costs squeeze {company} margins hard', impact: -0.06 },
        { text: 'Consumer spending drop hits {company} revenues', impact: -0.07 },
        { text: 'Rate hike fears pressure {company} stock', impact: -0.05 },
        { text: '{company} warns of margin compression ahead', impact: -0.04 },
      ],
      major: [
        { text: 'BREAKING: Fed announces emergency 75bps rate hike', impact: -0.12, global: true },
        { text: 'BREAKING: Inflation shows signs of cooling down', impact: 0.10, global: true },
      ]
    }
  },
  worldwar: {
    name: 'World War',
    icon: Warning,
    color: 'from-red-600 to-orange-600',
    desc: 'Geopolitical tensions and supply disruptions',
    news: {
      positive: [
        { text: '{company} wins major government contract', impact: 0.07, sectors: ['Defense'] },
        { text: '{company} secures domestic supply chain routes', impact: 0.05 },
        { text: 'Safe-haven demand boosts {company}', impact: 0.04 },
        { text: '{company} partners with ally nation on deal', impact: 0.03 },
      ],
      negative: [
        { text: '{company} supply routes disrupted by conflict', impact: -0.08 },
        { text: 'New sanctions impact {company} operations', impact: -0.07 },
        { text: 'Global uncertainty hits {company} hard', impact: -0.06 },
        { text: '{company} halts operations in conflict zone', impact: -0.05 },
      ],
      major: [
        { text: 'BREAKING: Major military escalation reported', impact: -0.14, global: true },
        { text: 'BREAKING: Peace talks show significant progress', impact: 0.12, global: true },
      ]
    }
  },
  techboom: {
    name: 'Tech Boom',
    icon: Cpu,
    color: 'from-purple-600 to-blue-600',
    desc: 'Rapid growth, hype cycles, and corrections',
    news: {
      positive: [
        { text: '{company} AI breakthrough announced', impact: 0.08, sectors: ['Tech'] },
        { text: '{company} user growth exceeds expectations', impact: 0.06 },
        { text: 'Investors pile into {company} on momentum', impact: 0.05 },
        { text: '{company} launches viral new product', impact: 0.04 },
      ],
      negative: [
        { text: '{company} valuations questioned by analysts', impact: -0.07 },
        { text: 'Tech rotation hits {company} stock', impact: -0.06, sectors: ['Tech'] },
        { text: '{company} growth slowing warns CEO', impact: -0.05 },
        { text: '{company} faces regulatory scrutiny', impact: -0.04 },
      ],
      major: [
        { text: 'BREAKING: Tech bubble fears spark market selloff', impact: -0.15, global: true },
        { text: 'BREAKING: AI optimism sends markets soaring', impact: 0.12, global: true },
      ]
    }
  },
  earnings: {
    name: 'Earnings Frenzy',
    icon: CalendarCheck,
    color: 'from-green-600 to-emerald-600',
    desc: 'Constant earnings surprises and announcements',
    news: {
      positive: [
        { text: '{company} CRUSHES earnings estimates!', impact: 0.08 },
        { text: '{company} raises full-year outlook', impact: 0.06 },
        { text: '{company} announces special dividend', impact: 0.04 },
        { text: '{company} reports record revenue quarter', impact: 0.05 },
      ],
      negative: [
        { text: '{company} MISSES on revenue badly', impact: -0.08 },
        { text: '{company} warns of weak next quarter', impact: -0.06 },
        { text: '{company} CEO unexpectedly sells shares', impact: -0.05 },
        { text: '{company} downgrades guidance for year', impact: -0.07 },
      ],
      major: [
        { text: 'BREAKING: {company} accounting scandal!', impact: -0.20 },
        { text: 'BREAKING: {company} announces 10-for-1 split!', impact: 0.12 },
      ]
    }
  },
  aibubble: {
    name: 'AI Bubble Burst',
    icon: ChartLineDown,
    color: 'from-gray-600 to-red-800',
    desc: 'The AI hype collapses — survive the crash',
    news: {
      positive: [
        { text: '{company} finds real AI revenue stream', impact: 0.05 },
        { text: '{company} called "fortress" by analysts', impact: 0.04 },
        { text: '{company} seen as undervalued after selloff', impact: 0.06 },
        { text: 'Bottom-fishers push {company} higher', impact: 0.03 },
      ],
      negative: [
        { text: '{company} AI product fails to deliver', impact: -0.08 },
        { text: '{company} announces massive layoffs', impact: -0.09 },
        { text: '{company} investors flee as losses mount', impact: -0.07 },
        { text: 'Analysts slash {company} price target', impact: -0.06 },
      ],
      major: [
        { text: 'CRASH: Markets enter AI winter territory', impact: -0.18, global: true },
        { text: 'RELIEF: Government announces tech stimulus', impact: 0.14, global: true },
      ]
    }
  }
};

// Level config: difficulty scaling
const getLevelConfig = (level) => {
  if (level <= 10) return { newsSpeed: 8000, volatility: 0.015, target: 2, time: 90, majorChance: 0.05 };
  if (level <= 20) return { newsSpeed: 7000, volatility: 0.02, target: 4, time: 90, majorChance: 0.07 };
  if (level <= 30) return { newsSpeed: 6000, volatility: 0.025, target: 6, time: 75, majorChance: 0.08 };
  if (level <= 40) return { newsSpeed: 4500, volatility: 0.03, target: 8, time: 60, majorChance: 0.10 };
  return { newsSpeed: 3000, volatility: 0.035, target: 10, time: 60, majorChance: 0.12 };
};

// Pick 5 companies for a level
const getCompaniesForLevel = (level) => {
  const offset = ((level - 1) * 3) % CHAOS_COMPANIES.length;
  const picked = [];
  for (let i = 0; i < 5; i++) {
    picked.push({ ...CHAOS_COMPANIES[(offset + i) % CHAOS_COMPANIES.length] });
  }
  // Scale base prices slightly per level for variety
  return picked.map(c => ({ ...c, basePrice: Math.round(c.basePrice * (0.9 + (level % 5) * 0.05)) }));
};

const ChaosMode = () => {
  const { fetchUser } = useAuth();
  const navigate = useNavigate();
  const [screen, setScreen] = useState('scenarios'); // scenarios, levels, playing, result
  const [scenario, setScenario] = useState(null);
  const [level, setLevel] = useState(1);
  const [progress, setProgress] = useState({});

  // Game state
  const [stocks, setStocks] = useState([]);
  const [selectedStock, setSelectedStock] = useState(0);
  const [cash, setCash] = useState(10000);
  const [positions, setPositions] = useState({});
  const [news, setNews] = useState([]);
  const [timeLeft, setTimeLeft] = useState(90);
  const [decisions, setDecisions] = useState([]);
  const gameLoop = useRef(null);
  const newsLoop = useRef(null);

  useEffect(() => {
    fetchProgress();
  }, []);

  const fetchProgress = async () => {
    try {
      const res = await api.get('/gamemode/chaos/progress');
      setProgress(res.data.progress?.scenarios || {});
    } catch { /* ignore */ }
  };

  const selectScenario = (key) => {
    setScenario(key);
    setScreen('levels');
  };

  const startLevel = (lvl) => {
    const config = getLevelConfig(lvl);
    const companies = getCompaniesForLevel(lvl);
    const initial = companies.map(c => ({
      ...c,
      price: c.basePrice,
      history: [{ t: 0, price: c.basePrice }],
      change: 0,
    }));

    setLevel(lvl);
    setStocks(initial);
    setCash(10000);
    setPositions({});
    setNews([]);
    setTimeLeft(config.time);
    setDecisions([]);
    setSelectedStock(0);
    setScreen('playing');
  };

  // Game loop
  useEffect(() => {
    if (screen !== 'playing') return;
    let t = 0;
    const config = getLevelConfig(level);

    gameLoop.current = setInterval(() => {
      t++;
      setStocks(prev => prev.map(s => {
        const rand = (Math.random() - 0.5) * config.volatility * s.price;
        const np = Math.max(1, s.price + rand);
        return { ...s, price: np, change: ((np - s.basePrice) / s.basePrice) * 100, history: [...s.history, { t, price: np }].slice(-60) };
      }));
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(gameLoop.current);
          clearInterval(newsLoop.current);
          setScreen('result');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(gameLoop.current);
  }, [screen, level]);

  // News loop
  useEffect(() => {
    if (screen !== 'playing' || !scenario) return;
    const sc = SCENARIOS[scenario];
    const config = getLevelConfig(level);

    const gen = () => {
      const rand = Math.random();
      let cat = rand < config.majorChance ? 'major' : rand < 0.5 ? 'positive' : 'negative';
      const templates = sc.news[cat];
      const tmpl = templates[Math.floor(Math.random() * templates.length)];
      const target = tmpl.global ? null : stocks[Math.floor(Math.random() * stocks.length)];

      setNews(prev => [{
        id: Date.now(),
        text: tmpl.text.replace('{company}', target?.name || 'Markets'),
        cat,
        isMajor: cat === 'major',
        symbol: target?.symbol,
        time: new Date().toLocaleTimeString(),
      }, ...prev].slice(0, 8));

      setStocks(prev => prev.map(s => {
        let imp = 0;
        if (tmpl.global) {
          imp = tmpl.impact * (0.5 + Math.random() * 0.5);
          if (tmpl.sectors?.includes(s.sector)) imp *= 1.5;
        } else if (s.symbol === target?.symbol) {
          imp = tmpl.impact;
        }
        return imp ? { ...s, price: Math.max(1, s.price * (1 + imp)) } : s;
      }));
    };

    setTimeout(gen, 2000);
    newsLoop.current = setInterval(gen, config.newsSpeed);
    return () => clearInterval(newsLoop.current);
  }, [screen, scenario, level]);

  const handleBuy = (sym) => {
    const s = stocks.find(x => x.symbol === sym);
    if (!s || cash < s.price) return;
    const shares = Math.floor(Math.min(cash * 0.25, cash) / s.price);
    if (shares <= 0) return;
    const cost = shares * s.price;
    setCash(p => p - cost);
    setPositions(p => ({
      ...p,
      [sym]: {
        shares: (p[sym]?.shares || 0) + shares,
        avgPrice: p[sym] ? ((p[sym].shares * p[sym].avgPrice) + cost) / ((p[sym].shares || 0) + shares) : s.price
      }
    }));
    setDecisions(p => [...p, { type: 'BUY', symbol: sym, price: s.price, shares }]);
  };

  const handleSell = (sym) => {
    const pos = positions[sym];
    const s = stocks.find(x => x.symbol === sym);
    if (!pos || !s) return;
    const proceeds = pos.shares * s.price;
    const profit = proceeds - (pos.shares * pos.avgPrice);
    setCash(p => p + proceeds);
    setPositions(p => { const n = { ...p }; delete n[sym]; return n; });
    setDecisions(p => [...p, { type: 'SELL', symbol: sym, price: s.price, shares: pos.shares, profit }]);
  };

  const getPortfolioValue = () => cash + stocks.reduce((s, st) => s + (positions[st.symbol]?.shares || 0) * st.price, 0);

  const handleLevelComplete = async (passed) => {
    if (!passed) return;
    try {
      const res = await api.post('/gamemode/chaos/complete-level', { scenario, level });
      if (res.data.reward > 0) {
        toast.success(`All 50 levels complete! +$${res.data.reward.toLocaleString()} added to portfolio!`);
      }
      showXpToasts(res.data.xp_result, { refreshUser: fetchUser });
      await fetchProgress();
    } catch { /* ignore */ }
  };

  // Save progress when result screen shows
  const resultSaved = useRef(false);
  useEffect(() => {
    if (screen === 'result' && !resultSaved.current) {
      resultSaved.current = true;
      const pv = getPortfolioValue();
      const ret = ((pv - 10000) / 10000) * 100;
      const config = getLevelConfig(level);
      if (ret >= config.target) {
        handleLevelComplete(true);
      }
    }
    if (screen !== 'result') {
      resultSaved.current = false;
    }
  }, [screen]);

  // =================== SCENARIOS SCREEN ===================
  if (screen === 'scenarios') {
    return (
      <div className="min-h-screen noise-bg">
        <Navbar />
        <div className="max-w-4xl mx-auto px-6 py-10">
          <button onClick={() => navigate('/game-modes')} className="flex items-center gap-2 text-zinc-400 hover:text-white mb-8 transition-colors">
            <ArrowLeft size={20} /> Back to Game Modes
          </button>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
            <div className="inline-flex p-4 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 mb-5">
              <Flame size={48} weight="fill" className="text-white" />
            </div>
            <h1 className="text-4xl font-unbounded font-bold text-white mb-3">Chaos Mode</h1>
            <p className="text-zinc-400 max-w-lg mx-auto">
              50 levels per scenario. Survive volatile markets and prove your skill. Complete all 50 to earn <span className="text-gold font-bold">$3,000</span> for your portfolio.
            </p>
          </motion.div>

          <div className="grid gap-4">
            {Object.entries(SCENARIOS).map(([key, data], i) => {
              const Icon = data.icon;
              const sp = progress[key] || {};
              const completed = sp.completed?.length || 0;
              const rewarded = sp.reward_claimed;
              return (
                <motion.button
                  key={key}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.08 }}
                  onClick={() => selectScenario(key)}
                  className="flex items-center justify-between p-5 rounded-xl bg-zinc-900/50 border border-zinc-800 hover:border-orange-500/40 transition-all group"
                  data-testid={`scenario-${key}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${data.color} flex items-center justify-center`}>
                      <Icon size={28} weight="fill" className="text-white" />
                    </div>
                    <div className="text-left">
                      <h3 className="text-white font-bold text-lg">{data.name}</h3>
                      <p className="text-zinc-500 text-sm">{data.desc}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-zinc-600">{completed}/50 levels</span>
                        {rewarded && <span className="text-xs text-gold font-bold flex items-center gap-1"><CheckCircle size={12} weight="fill" /> $3,000 claimed</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {/* Progress bar */}
                    <div className="w-24 h-2 bg-zinc-800 rounded-full overflow-hidden hidden sm:block">
                      <div className="h-full bg-gradient-to-r from-orange-500 to-yellow-400 rounded-full transition-all" style={{ width: `${(completed / 50) * 100}%` }} />
                    </div>
                    <ArrowRight size={20} className="text-zinc-600 group-hover:text-orange-400 transition-colors" />
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // =================== LEVELS SCREEN ===================
  if (screen === 'levels') {
    const sc = SCENARIOS[scenario];
    const Icon = sc.icon;
    const sp = progress[scenario] || { current_level: 1, completed: [] };
    const currentLvl = sp.current_level || 1;
    const completedLevels = sp.completed || [];

    return (
      <div className="min-h-screen noise-bg">
        <Navbar />
        <div className="max-w-4xl mx-auto px-6 py-8">
          <button onClick={() => setScreen('scenarios')} className="flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors">
            <ArrowLeft size={20} /> Back to Scenarios
          </button>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-4 mb-8">
            <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${sc.color} flex items-center justify-center`}>
              <Icon size={28} weight="fill" className="text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-unbounded font-bold text-white">{sc.name}</h1>
              <p className="text-zinc-400 text-sm">{completedLevels.length}/50 completed — {currentLvl <= 50 ? `Level ${currentLvl} unlocked` : 'All complete!'}</p>
            </div>
          </motion.div>

          <div className="grid grid-cols-5 sm:grid-cols-10 gap-2">
            {Array.from({ length: 50 }, (_, i) => i + 1).map(lvl => {
              const done = completedLevels.includes(lvl);
              const unlocked = lvl <= currentLvl;
              const isCurrent = lvl === currentLvl && !done;
              return (
                <motion.button
                  key={lvl}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: lvl * 0.01 }}
                  disabled={!unlocked}
                  onClick={() => unlocked && startLevel(lvl)}
                  className={`aspect-square rounded-xl flex items-center justify-center font-bold text-sm transition-all ${
                    done ? 'bg-emerald-500/20 border-2 border-emerald-500 text-emerald-400' :
                    isCurrent ? 'bg-orange-500/20 border-2 border-orange-500 text-orange-400 animate-pulse' :
                    unlocked ? 'bg-zinc-800 border border-zinc-700 text-white hover:border-orange-500/50' :
                    'bg-zinc-900/30 border border-zinc-800/50 text-zinc-700'
                  }`}
                  data-testid={`chaos-level-${lvl}`}
                >
                  {done ? <CheckCircle size={18} weight="fill" /> : !unlocked ? <Lock size={14} /> : lvl}
                </motion.button>
              );
            })}
          </div>

          {/* Reward info */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="mt-8 glass-card p-5 border-l-4 border-l-gold">
            <div className="flex items-center gap-2 mb-1">
              <Trophy size={20} weight="fill" className="text-gold" />
              <span className="text-gold font-bold">Completion Reward</span>
            </div>
            <p className="text-zinc-400 text-sm">Complete all 50 levels to earn <span className="text-gold font-bold">+$3,000</span> added to your main portfolio.</p>
          </motion.div>

          {/* Difficulty legend */}
          <div className="mt-4 flex flex-wrap gap-3 text-xs text-zinc-500">
            <span>Lv 1-10: Easy</span>
            <span>Lv 11-20: Medium</span>
            <span>Lv 21-30: Hard</span>
            <span>Lv 31-40: Expert</span>
            <span>Lv 41-50: Extreme</span>
          </div>
        </div>
      </div>
    );
  }

  // =================== PLAYING SCREEN ===================
  if (screen === 'playing') {
    const config = getLevelConfig(level);
    const cs = stocks[selectedStock];
    const pos = positions[cs?.symbol];
    const pv = getPortfolioValue();
    const sc = SCENARIOS[scenario];
    const ScIcon = sc?.icon;

    return (
      <div className="min-h-screen noise-bg">
        <div className="max-w-7xl mx-auto px-4 py-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg bg-gradient-to-br ${sc.color}`}>
                <ScIcon size={22} className="text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">{sc.name} — Level {level}</h2>
                <p className="text-zinc-500 text-xs">Target: +{config.target}% profit to pass</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="text-zinc-500 text-xs">Portfolio</p>
                <p className={`font-bold text-sm ${pv >= 10000 ? 'text-emerald-400' : 'text-red-400'}`}>${pv.toFixed(0)}</p>
              </div>
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg ${timeLeft <= 15 ? 'bg-red-500/20 text-red-400 animate-pulse' : 'bg-zinc-800 text-white'}`}>
                <Clock size={16} />
                <span className="font-mono font-bold">{timeLeft}s</span>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-4 gap-3">
            {/* Stock list */}
            <div className="space-y-2">
              {stocks.map((s, i) => {
                const p = positions[s.symbol];
                return (
                  <button key={s.symbol} onClick={() => setSelectedStock(i)}
                    className={`w-full p-3 rounded-xl text-left transition-all ${selectedStock === i ? 'bg-orange-500/20 border-2 border-orange-500' : 'bg-zinc-900/50 border border-zinc-800 hover:border-zinc-600'}`}
                    data-testid={`chaos-stock-${s.symbol}`}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-white font-bold text-sm">{s.symbol}</p>
                        <p className="text-zinc-500 text-xs">{s.sector}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-white font-mono text-sm">${s.price.toFixed(2)}</p>
                        <p className={`text-xs ${s.change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{s.change >= 0 ? '+' : ''}{s.change.toFixed(1)}%</p>
                      </div>
                    </div>
                    {p && <p className="text-yellow-400 text-xs mt-1 pt-1 border-t border-zinc-700">{p.shares} shares @ ${p.avgPrice.toFixed(2)}</p>}
                  </button>
                );
              })}
              <div className="p-3 rounded-xl bg-zinc-900/50 border border-zinc-800">
                <p className="text-zinc-500 text-xs">Cash</p>
                <p className="text-emerald-400 font-bold">${cash.toFixed(0)}</p>
              </div>
            </div>

            {/* Chart */}
            <div className="lg:col-span-2 bg-zinc-900/50 rounded-2xl p-4 border border-zinc-800">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="text-white font-bold">{cs?.name}</h3>
                  <p className="text-2xl font-bold text-white">${cs?.price.toFixed(2)}</p>
                  <p className={`text-sm ${cs?.change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{cs?.change >= 0 ? '+' : ''}{cs?.change.toFixed(2)}%</p>
                </div>
                {pos && (
                  <div className="text-right bg-yellow-500/10 px-3 py-2 rounded-lg">
                    <p className="text-yellow-400 text-sm font-bold">{pos.shares} shares</p>
                    <p className="text-zinc-400 text-xs">P/L: ${((cs.price - pos.avgPrice) * pos.shares).toFixed(2)}</p>
                  </div>
                )}
              </div>
              <div className="h-44 mb-3">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={cs?.history || []}>
                    <XAxis dataKey="t" hide />
                    <YAxis domain={['auto', 'auto']} hide />
                    <Line type="monotone" dataKey="price" stroke={cs?.change >= 0 ? '#10b981' : '#ef4444'} strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="flex gap-3">
                <Button onClick={() => handleBuy(cs?.symbol)} disabled={cash < (cs?.price || 0)} className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50" data-testid="chaos-buy-btn">
                  <TrendUp size={18} className="mr-2" /> BUY
                </Button>
                <Button onClick={() => handleSell(cs?.symbol)} disabled={!pos} className="flex-1 py-3 bg-red-600 hover:bg-red-700 disabled:opacity-50" data-testid="chaos-sell-btn">
                  <TrendDown size={18} className="mr-2" /> SELL
                </Button>
              </div>
            </div>

            {/* News */}
            <div className="bg-zinc-900/50 rounded-2xl p-4 border border-zinc-800">
              <div className="flex items-center gap-2 mb-3">
                <Lightning size={18} className="text-yellow-400" />
                <h3 className="text-white font-bold text-sm">Live News</h3>
              </div>
              <div className="space-y-2 max-h-[380px] overflow-y-auto">
                <AnimatePresence>
                  {news.map(n => (
                    <motion.div key={n.id} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
                      className={`p-2.5 rounded-lg text-xs ${n.isMajor ? 'bg-yellow-500/20 border border-yellow-500/50' : n.cat === 'positive' ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-red-500/10 border border-red-500/20'}`}
                    >
                      <p className="text-white">{n.text}</p>
                      <p className="text-zinc-500 mt-0.5">{n.time}</p>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // =================== RESULT SCREEN ===================
  if (screen === 'result') {
    const pv = getPortfolioValue();
    const ret = ((pv - 10000) / 10000) * 100;
    const config = getLevelConfig(level);
    const passed = ret >= config.target;
    const sc = SCENARIOS[scenario];

    return (
      <div className="min-h-screen noise-bg">
        <Navbar />
        <div className="max-w-2xl mx-auto px-6 py-12">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center">
            <div className={`inline-flex p-6 rounded-full ${passed ? 'bg-emerald-500/20' : 'bg-red-500/20'} mb-6`}>
              {passed ? <Trophy size={64} weight="fill" className="text-emerald-400" /> : <Warning size={64} weight="fill" className="text-red-400" />}
            </div>

            <h1 className="text-3xl font-unbounded font-bold text-white mb-2">
              Level {level} — {passed ? 'Passed!' : 'Failed'}
            </h1>
            <p className="text-zinc-400 mb-2">{sc.name}</p>
            <p className="text-zinc-500 text-sm mb-8">
              {passed ? `You hit +${ret.toFixed(1)}% (target: +${config.target}%). Nice work!` : `You needed +${config.target}% but got ${ret >= 0 ? '+' : ''}${ret.toFixed(1)}%. Try again!`}
            </p>

            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="bg-zinc-900/50 rounded-xl p-5 border border-zinc-800">
                <p className="text-zinc-500 text-sm">Final Portfolio</p>
                <p className="text-xl font-bold text-white">${pv.toFixed(0)}</p>
              </div>
              <div className="bg-zinc-900/50 rounded-xl p-5 border border-zinc-800">
                <p className="text-zinc-500 text-sm">Return</p>
                <p className={`text-xl font-bold ${ret >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{ret >= 0 ? '+' : ''}{ret.toFixed(1)}%</p>
              </div>
              <div className="bg-zinc-900/50 rounded-xl p-5 border border-zinc-800">
                <p className="text-zinc-500 text-sm">Trades Made</p>
                <p className="text-xl font-bold text-white">{decisions.length}</p>
              </div>
              <div className="bg-zinc-900/50 rounded-xl p-5 border border-zinc-800">
                <p className="text-zinc-500 text-sm">Profitable</p>
                <p className="text-xl font-bold text-yellow-400">{decisions.filter(d => d.profit > 0).length}</p>
              </div>
            </div>

            <div className="flex gap-4">
              <Button onClick={() => startLevel(level)} className="flex-1 py-4 bg-zinc-800 hover:bg-zinc-700" data-testid="chaos-retry-btn">
                {passed ? 'Replay' : 'Retry'} Level {level}
              </Button>
              {passed && level < 50 ? (
                <Button onClick={() => startLevel(level + 1)} className="flex-1 py-4 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700" data-testid="chaos-next-btn">
                  Next Level <ArrowRight size={18} className="ml-2" />
                </Button>
              ) : (
                <Button onClick={() => setScreen('levels')} className="flex-1 py-4 bg-orange-600 hover:bg-orange-700">
                  Back to Levels
                </Button>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return null;
};

export default ChaosMode;
